import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellIcon,
  XMarkIcon,
  // CheckIcon,
  // ClockIcon,
  TruckIcon,
  GiftIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import { supabase, TABLES } from "../supabase";
import { cn } from "../utils/cn";

interface Notification {
  id: string;
  userId: string;
  type:
    | "food_request"
    | "food_claim"
    | "delivery_request"
    | "request_accepted"
    | "request_declined"
    | "delivery_assigned"
    | "delivery_completed"
    | "event_created"
    | "event_joined"
    | "event_left"
    | "event_joined_confirmation";
  title: string;
  message: string;
  is_read: boolean;
  createdAt: any;
  relatedId?: string;
  actionData?: any;
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [loading, setLoading] = useState(false);
  const [requestDetails, setRequestDetails] = useState<any>(null);

  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      const { data, error } = await supabase
        .from(TABLES.NOTIFICATIONS)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching notifications:", error);
      } else {
        setNotifications(data || []);
      }
    };

    fetchNotifications();

    // Set up real-time subscription
    const subscription = supabase
      .channel("notifications")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: TABLES.NOTIFICATIONS,
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from(TABLES.NOTIFICATIONS)
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read first
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }

      // Close notification panel
      setIsOpen(false);

      // Show a brief loading state
      setLoading(true);

      // Navigate based on notification type
      switch (notification.type) {
        case "food_request":
        case "food_claim":
          // Navigate to donor dashboard to see claims/requests
          navigate("/dashboard/donor");
          break;

        case "request_accepted":
        case "request_declined":
          // Navigate to recipient dashboard to see status
          navigate("/dashboard/recipient");
          break;

        case "delivery_request":
        case "delivery_assigned":
        case "delivery_completed":
          // Navigate to volunteer dashboard
          navigate("/dashboard/volunteer");
          break;

        case "event_created":
        case "event_joined":
        case "event_left":
        case "event_joined_confirmation":
          // Navigate to community page
          navigate("/community");
          break;

        default:
          // For other notifications, try to navigate to the appropriate dashboard based on user role
          if (user?.role === "donor") {
            navigate("/dashboard/donor");
          } else if (user?.role === "recipient") {
            navigate("/dashboard/recipient");
          } else if (user?.role === "volunteer") {
            navigate("/dashboard/volunteer");
          } else {
            navigate("/");
          }
          break;
      }

      // Ensure scroll to top after navigation
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    } catch (error) {
      console.error("Error handling notification click:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!selectedNotification || !requestDetails) return;

    setLoading(true);
    try {
      let tableName = "";
      if (selectedNotification.type === "food_request") {
        tableName = TABLES.FOOD_REQUESTS;
      } else if (selectedNotification.type === "food_claim") {
        tableName = TABLES.FOOD_CLAIMS;
      } else if (selectedNotification.type === "delivery_request") {
        tableName = TABLES.DELIVERY_REQUESTS;
      }

      // Update request status
      const { error: updateError } = await supabase
        .from(tableName)
        .update({ status: "accepted" })
        .eq("id", requestDetails.id);

      if (updateError) throw updateError;

      // Create notification for requester
      await createNotification(
        requestDetails.requester_id || requestDetails.claimant_id,
        "request_accepted",
        "Request Accepted!",
        `Your ${selectedNotification.type.replace(
          "_",
          " "
        )} has been accepted.`,
        requestDetails.id
      );

      setSelectedNotification(null);
      setRequestDetails(null);
    } catch (error) {
      console.error("Error accepting request:", error);
    }
    setLoading(false);
  };

  const handleDeclineRequest = async () => {
    if (!selectedNotification || !requestDetails) return;

    setLoading(true);
    try {
      let tableName = "";
      if (selectedNotification.type === "food_request") {
        tableName = TABLES.FOOD_REQUESTS;
      } else if (selectedNotification.type === "food_claim") {
        tableName = TABLES.FOOD_CLAIMS;
      } else if (selectedNotification.type === "delivery_request") {
        tableName = TABLES.DELIVERY_REQUESTS;
      }

      // Update request status
      const { error: updateError } = await supabase
        .from(tableName)
        .update({ status: "declined" })
        .eq("id", requestDetails.id);

      if (updateError) throw updateError;

      // Create notification for requester
      await createNotification(
        requestDetails.requester_id || requestDetails.claimant_id,
        "request_declined",
        "Request Declined",
        `Your ${selectedNotification.type.replace(
          "_",
          " "
        )} has been declined.`,
        requestDetails.id
      );

      setSelectedNotification(null);
      setRequestDetails(null);
    } catch (error) {
      console.error("Error declining request:", error);
    }
    setLoading(false);
  };

  const createNotification = async (
    userId: string,
    type: string,
    title: string,
    message: string,
    relatedId?: string
  ) => {
    try {
      const { error } = await supabase.from(TABLES.NOTIFICATIONS).insert({
        user_id: userId,
        type,
        title,
        message,
        is_read: false,
        created_at: new Date().toISOString(),
        related_id: relatedId,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error creating notification:", error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "food_request":
      case "food_claim":
        return <GiftIcon className="w-5 h-5" />;
      case "delivery_request":
      case "delivery_assigned":
      case "delivery_completed":
        return <TruckIcon className="w-5 h-5" />;
      case "event_created":
      case "event_joined":
      case "event_left":
      case "event_joined_confirmation":
        return <CalendarIcon className="w-5 h-5" />;
      default:
        return <BellIcon className="w-5 h-5" />;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return "";
    if (date.toDate) return date.toDate().toLocaleString();
    return new Date(date).toLocaleString();
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 transition-colors hover:text-emerald-600"
        aria-label="Notifications"
      >
        <BellIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-emerald-500 rounded-full">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 bg-white/95 backdrop-blur-2xl border border-white/30 rounded-xl shadow-xl w-80 top-full">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                Notifications
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-96">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <BellIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "p-4 border-b border-slate-100 cursor-pointer hover:bg-emerald-50/50 transition-all duration-200 group",
                    !notification.is_read && "bg-emerald-50/30"
                  )}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={cn(
                        "p-2 rounded-lg",
                        notification.is_read
                          ? "bg-slate-100 text-slate-600"
                          : "bg-emerald-100 text-emerald-600"
                      )}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          "text-sm font-medium",
                          notification.is_read
                            ? "text-slate-900"
                            : "text-slate-900 font-semibold"
                        )}
                      >
                        {notification.title}
                      </p>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {formatDate(notification.createdAt)}
                      </p>
                      <p className="mt-1 text-xs text-slate-400 group-hover:text-emerald-600 transition-colors">
                        Click to view details
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      )}
                      <div className="text-slate-400 group-hover:text-emerald-600 transition-colors">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Notification Details Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white/95 backdrop-blur-2xl rounded-xl shadow-2xl border border-white/30">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedNotification.title}
              </h3>
              <button
                onClick={() => {
                  setSelectedNotification(null);
                  setRequestDetails(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="text-center">
                  <div className="w-8 h-8 mx-auto mb-4 border-b-2 rounded-full animate-spin border-emerald-600"></div>
                  <p className="text-sm text-slate-600">Loading details...</p>
                </div>
              ) : requestDetails ? (
                <div className="space-y-4">
                  <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                    <h4 className="mb-3 text-sm font-medium text-slate-900">
                      Request Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Requester:</span>
                        <span className="font-medium text-slate-900">
                          {requestDetails.requesterName ||
                            requestDetails.claimantName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Email:</span>
                        <span className="text-slate-700">
                          {requestDetails.requesterEmail ||
                            requestDetails.claimantEmail}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Phone:</span>
                        <span className="text-slate-700">
                          {requestDetails.phone}
                        </span>
                      </div>
                      {requestDetails.quantity && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Quantity:</span>
                          <span className="text-slate-700">
                            {requestDetails.quantity} servings
                          </span>
                        </div>
                      )}
                      {requestDetails.totalPrice && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Price:</span>
                          <span className="font-medium text-slate-900">
                            LKR {requestDetails.totalPrice}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Status:</span>
                        <span
                          className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            requestDetails.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : requestDetails.status === "accepted"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-red-100 text-red-800"
                          )}
                        >
                          {requestDetails.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {requestDetails.notes && (
                    <div className="p-3 border border-slate-200 rounded-lg bg-slate-50">
                      <h5 className="mb-1 text-sm font-medium text-slate-900">
                        Notes:
                      </h5>
                      <p className="text-sm text-slate-700">
                        {requestDetails.notes}
                      </p>
                    </div>
                  )}

                  {requestDetails.status === "pending" && (
                    <div className="flex space-x-3">
                      <button
                        onClick={handleDeclineRequest}
                        className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                        disabled={loading}
                      >
                        Decline
                      </button>
                      <button
                        onClick={handleAcceptRequest}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                        disabled={loading}
                      >
                        Accept
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-slate-500">
                  <p className="text-sm">{selectedNotification.message}</p>
                  <p className="mt-2 text-xs text-slate-400">
                    {formatDate(selectedNotification.createdAt)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
